class CartReplacementError extends Error {
  constructor(message, code = 'UNKNOWN', originalError = null) {
    super(message);
    this.name = 'CartReplacementError';
    this.code = code;
    this.originalError = originalError;
  }
}

class NetworkError extends CartReplacementError {
  constructor(message, status = null, originalError = null) {
    super(message, 'NETWORK_ERROR', originalError);
    this.status = status;
  }
}

class ValidationError extends CartReplacementError {
  constructor(message, field = null) {
    super(message, 'VALIDATION_ERROR');
    this.field = field;
  }
}

class PartialReplacementError extends CartReplacementError {
  constructor(message, replacementId = null, oldItemId = null) {
    super(message, 'PARTIAL_REPLACEMENT_ERROR');
    this.replacementId = replacementId;
    this.oldItemId = oldItemId;
  }
}

/**
 * Cart Item Replacement library for silently replacing old cart items with new products
 */
class CartItemReplacement {
  CONFIG = {
    CART_API_URL: '/cart.js',
    CART_ADD_URL: '/cart/add.js',
    CART_CHANGE_URL: '/cart/change.js',
    TIMEOUT: 10000,
    RETRY_ATTEMPTS: 3,
    RETRY_DELAY: 1000,
    STORAGE_KEY: 'boarderie_cart_replacements_log',
    EVENTS: {
      initialized: 'cart-replacement:initialized',
      started: 'cart-replacement:started',
      completed: 'cart-replacement:completed',
      error: 'cart-replacement:error',
      itemReplaced: 'cart-replacement:item-replaced',
    }
  };

  #initialized = false;
  #initializationPromise = null;
  #replacementMappings = new Map();
  #logs = [];
  #errors = [];
  #isProcessing = false;

  constructor() {
    this.#initializationPromise = this.#initialize();
  }

  get initialized() {
    return this.#initialized;
  }

  get logs() {
    return [...this.#logs];
  }

  get errors() {
    return [...this.#errors];
  }

  get isProcessing() {
    return this.#isProcessing;
  }

  /**
   * Public method to wait for initialization
   * @returns {Promise<boolean>}
   */
  async waitForInitialization() {
    if (this.#initializationPromise) {
      await this.#initializationPromise;
    }
    return this.#initialized;
  }

  /**
   * Private initialization method
   * @private
   */
  async #initialize() {
    try {
      this.#log('info', 'Initializing CartItemReplacement');

      this.#loadReplacementMappings();

      this.#loadLogsFromStorage();

      if (this.#isDebugMode() && this.#logs.length > 0) {
        this.#displayLogsAsTable();
      }

      this.#initialized = true;

      this.#dispatchEvent(this.CONFIG.EVENTS.initialized, {
        mappingsCount: this.#replacementMappings.size,
        logsCount: this.#logs.length,
        timestamp: new Date().toISOString(),
      });

      this.#log('info', `Initialized with ${this.#replacementMappings.size} replacement mappings`);

      if (this.#replacementMappings.size > 0) {
        await this.processReplacements();
      }
    } catch (error) {
      this.#handleError(error);
      this.#dispatchEvent(this.CONFIG.EVENTS.error, {
        error: error.message,
        code: error.code || 'INITIALIZATION_ERROR',
        timestamp: new Date().toISOString(),
      });
    }
  }

  /**
   * Load replacement mappings from window.Boarderie
   * @private
   */
  #loadReplacementMappings() {
    if (!window.Boarderie?.cartItemReplacements) {
      this.#log('info', 'No cart item replacements configured');
      return;
    }

    const mappings = window.Boarderie.cartItemReplacements;

    if (!Array.isArray(mappings)) {
      throw new ValidationError('Cart item replacements must be an array');
    }

    mappings.forEach((mapping) => {
      if (typeof mapping !== 'string') {
        this.#log('warn', `Invalid mapping format: ${mapping}`);
        return;
      }

      const [oldVariantId, newVariantId] = mapping.split(':').map(id => id.trim());

      if (!oldVariantId || !newVariantId) {
        this.#log('warn', `Invalid mapping format: ${mapping}`);
        return;
      }

      this.#replacementMappings.set(oldVariantId, newVariantId);
    });

    this.#log('info', `Loaded ${this.#replacementMappings.size} replacement mappings`);
  }

  /**
   * Load logs from localStorage
   * @private
   */
  #loadLogsFromStorage() {
    try {
      const storedLogs = localStorage.getItem(this.CONFIG.STORAGE_KEY);
      if (storedLogs) {
        this.#logs = JSON.parse(storedLogs);
        this.#log('info', `Loaded ${this.#logs.length} logs from storage`);
      }
    } catch (error) {
      this.#log('error', 'Failed to load logs from storage', error);
    }
  }

  /**
   * Save logs to localStorage
   * @private
   */
  #saveLogsToStorage() {
    try {
      localStorage.setItem(this.CONFIG.STORAGE_KEY, JSON.stringify(this.#logs));
    } catch (error) {
      this.#log('error', 'Failed to save logs to storage', error);
    }
  }

  /**
   * Display logs as a table in console
   * @private
   */
  #displayLogsAsTable() {
    console.log('%c[CartItemReplacement] Replacement History', 'font-weight: bold; font-size: 14px;');

    const tableData = this.#logs.map((log, index) => ({
      '#': index + 1,
      'Timestamp': new Date(log.timestamp).toLocaleString(),
      'Old Item ID': log.oldItem.id,
      'Old Quantity': log.oldItem.quantity,
      'Replaced Item ID': log.replacedItem.id,
      'Replaced Quantity': log.replacedItem.quantity,
      'Status': log.status || 'success',
    }));

    console.table(tableData);
  }

  /**
   * Public method to display logs
   * @public
   */
  displayLogs() {
    if (this.#logs.length === 0) {
      console.log('%c[CartItemReplacement] No replacement history', 'color: #999;');
      return;
    }
    this.#displayLogsAsTable();
  }

  /**
   * Process all cart item replacements
   * @public
   * @returns {Promise<Object>}
   */
  async processReplacements() {
    if (this.#isProcessing) {
      this.#log('warn', 'Replacement process already running');
      return { success: false, message: 'Already processing' };
    }

    if (this.#replacementMappings.size === 0) {
      this.#log('info', 'No replacements to process');
      return { success: true, message: 'No replacements needed' };
    }

    this.#isProcessing = true;
    this.#dispatchEvent(this.CONFIG.EVENTS.started, {
      timestamp: new Date().toISOString(),
    });

    try {
      let cart = await this.#fetchCart();

      const partialErrorsFixed = await this.#fixPartialErrors(cart);
      if (partialErrorsFixed > 0) {
        this.#log('info', `Fixed ${partialErrorsFixed} partial errors`);
        cart = await this.#fetchCart();
      }

      const itemsToReplace = this.#findItemsToReplace(cart);

      if (itemsToReplace.length === 0) {
        this.#log('info', 'No items in cart need replacement');
        this.#isProcessing = false;
        return { success: true, message: 'No items need replacement' };
      }

      this.#log('info', `Found ${itemsToReplace.length} items to replace`);

      const results = {
        total: itemsToReplace.length,
        succeeded: 0,
        failed: 0,
        errors: [],
      };

      for (const item of itemsToReplace) {
        try {
          await this.#replaceCartItem(item);
          results.succeeded++;
        } catch (error) {
          results.failed++;
          results.errors.push({
            item,
            error: error.message,
          });
          this.#handleError(error);
        }
      }

      this.#dispatchEvent(this.CONFIG.EVENTS.completed, {
        results,
        timestamp: new Date().toISOString(),
      });

      this.#log('info', `Replacement process completed: ${results.succeeded} succeeded, ${results.failed} failed`);

      this.#isProcessing = false;
      return { success: true, results };
    } catch (error) {
      this.#isProcessing = false;
      this.#handleError(error);
      throw error;
    }
  }

  /**
   * Fix partial errors by removing old items that weren't removed during initial replacement
   * @private
   * @param {Object} cart - Current cart object
   * @returns {Promise<number>} Number of partial errors fixed
   */
  async #fixPartialErrors(cart) {
    const partialErrors = this.#logs.filter(log => log.status === 'partial');

    console.log("partialErrors", partialErrors);

    if (partialErrors.length === 0) {
      return 0;
    }

    this.#log('info', `Found ${partialErrors.length} partial errors to fix`);

    let fixedCount = 0;

    for (const logEntry of partialErrors) {
      const oldItemKey = logEntry.oldItem.key;
      const oldItemId = logEntry.oldItem.id;

      const itemStillInCart = cart.items?.find(item => item.key === oldItemKey);

      if (!itemStillInCart) {
        this.#log('info', `Old item ${oldItemId} (key: ${oldItemKey}) already removed, updating log status`);
        logEntry.status = 'success';
        delete logEntry.error;
        fixedCount++;
        continue;
      }

      this.#log('info', `Attempting to remove old item ${oldItemId} (key: ${oldItemKey}) from partial error`);

      try {
        await this.#removeFromCart(oldItemKey);
        this.#log('info', `Successfully removed old item ${oldItemId}, updating log status`);

        logEntry.status = 'success';
        delete logEntry.error;
        fixedCount++;
      } catch (error) {
        this.#log('error', `Failed to fix partial error for item ${oldItemId}`, error);
      }
    }

    if (fixedCount > 0) {
      this.#saveLogsToStorage();
    }

    return fixedCount;
  }

  /**
   * Fetch current cart
   * @private
   * @returns {Promise<Object>}
   */
  async #fetchCart() {
    const response = await this.#fetchWithRetry(this.CONFIG.CART_API_URL);
    return response;
  }

  /**
   * Find items in cart that need replacement
   * @private
   * @param {Object} cart - Cart object
   * @returns {Array}
   */
  #findItemsToReplace(cart) {
    if (!cart?.items || !Array.isArray(cart.items)) {
      return [];
    }

    return cart.items.filter(item => {
      const variantId = String(item.variant_id || item.id);
      return this.#replacementMappings.has(variantId);
    });
  }

  /**
   * Replace a single cart item
   * @private
   * @param {Object} oldItem - Cart item to replace
   * @returns {Promise<void>}
   */
  async #replaceCartItem(oldItem) {
    const oldVariantId = String(oldItem.variant_id || oldItem.id);
    const newVariantId = this.#replacementMappings.get(oldVariantId);

    this.#log('info', `Replacing item ${oldVariantId} with ${newVariantId}`);

    // Check if this replacement was already done (to avoid duplicates)
    if (this.#wasAlreadyReplaced(oldVariantId, newVariantId, oldItem.key)) {
      this.#log('info', `Item ${oldVariantId} was already replaced, skipping`);
      return;
    }

    const replacementData = {
      id: newVariantId,
      quantity: oldItem.quantity,
      properties: oldItem.properties || {},
    };

    let addSuccess = false;
    let removeSuccess = false;

    try {
      // Step 1: Add replacement product to cart
      this.#log('info', `Adding replacement product ${newVariantId}`);
      await this.#addToCart(replacementData);
      addSuccess = true;
      this.#log('info', `Successfully added replacement product ${newVariantId}`);

      // Step 2: Remove old product from cart
      this.#log('info', `Removing old product ${oldVariantId}`);
      await this.#removeFromCart(oldItem.key);
      removeSuccess = true;
      this.#log('info', `Successfully removed old product ${oldVariantId}`);

      // Step 3: Log the successful replacement
      const logEntry = {
        timestamp: new Date().toISOString(),
        status: 'success',
        oldItem: {
          id: oldVariantId,
          key: oldItem.key,
          quantity: oldItem.quantity,
          properties: oldItem.properties || {},
        },
        replacedItem: {
          id: newVariantId,
          quantity: oldItem.quantity,
          properties: oldItem.properties || {},
        },
      };

      this.#logs.push(logEntry);
      this.#saveLogsToStorage();

      this.#dispatchEvent(this.CONFIG.EVENTS.itemReplaced, {
        logEntry,
        timestamp: new Date().toISOString(),
      });

    } catch (error) {
      if (addSuccess && !removeSuccess) {
        // Critical case: Added replacement but failed to remove old item
        const partialError = new PartialReplacementError(
          `Added replacement ${newVariantId} but failed to remove old item ${oldVariantId}`,
          newVariantId,
          oldVariantId
        );

        // Log the partial replacement
        const logEntry = {
          timestamp: new Date().toISOString(),
          status: 'partial',
          error: error.message,
          oldItem: {
            id: oldVariantId,
            key: oldItem.key,
            quantity: oldItem.quantity,
            properties: oldItem.properties || {},
          },
          replacedItem: {
            id: newVariantId,
            quantity: oldItem.quantity,
            properties: oldItem.properties || {},
          },
        };

        this.#logs.push(logEntry);
        this.#saveLogsToStorage();

        throw partialError;
      }

      throw error;
    }
  }

  /**
   * Check if replacement was already done
   * @private
   * @param {string} oldVariantId
   * @param {string} newVariantId
   * @param {string} oldItemKey
   * @returns {boolean}
   */
  #wasAlreadyReplaced(oldVariantId, newVariantId, oldItemKey) {
    return this.#logs.some(log =>
      log.oldItem.id === oldVariantId &&
      log.oldItem.key === oldItemKey &&
      log.replacedItem.id === newVariantId &&
      (log.status === 'success' || log.status === 'partial')
    );
  }

  /**
   * Add item to cart
   * @private
   * @param {Object} data - Item data
   * @returns {Promise<Object>}
   */
  async #addToCart(data) {
    const response = await this.#fetchWithRetry(this.CONFIG.CART_ADD_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(data),
    });

    return response;
  }

  /**
   * Remove item from cart
   * @private
   * @param {string} key - Cart item key
   * @returns {Promise<Object>}
   */
  async #removeFromCart(key) {
    const response = await this.#fetchWithRetry(this.CONFIG.CART_CHANGE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        id: key,
        quantity: 0,
      }),
    });

    return response;
  }

  /**
   * Enhanced fetch with retry logic and exponential backoff
   * @private
   * @param {string} url - The URL to fetch
   * @param {Object} options - Fetch options
   * @param {number} attempt - Current attempt number
   * @returns {Promise<Object>} Response data
   */
  async #fetchWithRetry(url, options = {}, attempt = 1) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.CONFIG.TIMEOUT);

      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new NetworkError(
          `HTTP ${response.status}: ${response.statusText}`,
          response.status
        );
      }

      return await response.json();
    } catch (error) {
      if (attempt < this.CONFIG.RETRY_ATTEMPTS && this.#shouldRetry(error)) {
        this.#log('warn', `Attempt ${attempt} failed, retrying in ${this.#getRetryDelay(attempt)}ms`);
        await this.#delay(this.#getRetryDelay(attempt));
        return this.#fetchWithRetry(url, options, attempt + 1);
      }
      throw error;
    }
  }

  /**
   * Determines if an error should trigger a retry
   * @private
   * @param {Error} error - The error to check
   * @returns {boolean} Whether to retry
   */
  #shouldRetry(error) {
    return (
      error.name === 'AbortError' ||
      error.name === 'TypeError' ||
      (error instanceof NetworkError && error.status >= 500)
    );
  }

  /**
   * Calculates retry delay with exponential backoff
   * @private
   * @param {number} attempt - Current attempt number
   * @returns {number} Delay in milliseconds
   */
  #getRetryDelay(attempt) {
    return this.CONFIG.RETRY_DELAY * Math.pow(2, attempt - 1);
  }

  /**
   * Utility delay function
   * @private
   * @param {number} ms - Milliseconds to delay
   * @returns {Promise<void>}
   */
  #delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Enhanced error handling with categorization
   * @private
   * @param {Error} error - The error to handle
   */
  #handleError(error) {
    this.#errors.push({
      message: error.message,
      code: error.code || 'UNKNOWN',
      timestamp: new Date().toISOString(),
      stack: error.stack,
    });

    if (error instanceof ValidationError) {
      this.#log('warn', `Validation error: ${error.message}`, error);
    } else if (error instanceof NetworkError) {
      this.#log('error', `Network error: ${error.message}`, error);
    } else if (error instanceof PartialReplacementError) {
      this.#log('error', `Partial replacement error: ${error.message}`, error);
    } else {
      this.#log('error', `Unexpected error: ${error.message}`, error);
    }

    // Keep only last 20 errors to prevent memory leaks
    if (this.#errors.length > 20) {
      this.#errors = this.#errors.slice(-20);
    }
  }

  /**
   * Enhanced logging with levels
   * @private
   * @param {string} level - Log level (debug, info, warn, error)
   * @param {string} message - Log message
   * @param {*} data - Additional data to log
   */
  #log(level, message, data = null) {
    const timestamp = new Date().toISOString();
    const logMessage = `[${timestamp}] CartItemReplacement: ${message}`;

    if (level === 'debug' && !this.#isDebugMode()) {
      return;
    }

    switch (level) {
      case 'debug':
        console.debug(logMessage, data);
        break;
      case 'info':
        console.info(logMessage, data);
        break;
      case 'warn':
        console.warn(logMessage, data);
        break;
      case 'error':
        console.error(logMessage, data);
        break;
      default:
        console.log(logMessage, data);
    }
  }

  /**
   * Checks if debug mode is enabled
   * @private
   * @returns {boolean} Whether debug mode is active
   */
  #isDebugMode() {
    return (
      window.location.search.includes('debug=true') ||
      localStorage.getItem('cart-replacement-debug') === 'true' ||
      window.Shopify?.designMode
    );
  }

  /**
   * Dispatches a custom event
   * @private
   * @param {string} eventName - The name of the event
   * @param {Object} detail - The detail of the event
   */
  #dispatchEvent(eventName, detail) {
    try {
      const event = new CustomEvent(eventName, {
        detail: {
          ...detail,
          source: 'CartItemReplacement',
          version: '1.0.0',
        },
        bubbles: true,
        cancelable: true,
      });

      this.#log('debug', `Dispatching event: ${eventName}`, detail);
      document.dispatchEvent(event);
    } catch (error) {
      this.#log('error', `Failed to dispatch event: ${eventName}`, error);
    }
  }

  /**
   * Clear all logs
   * @public
   */
  clearLogs() {
    this.#logs = [];
    this.#saveLogsToStorage();
    this.#log('info', 'Logs cleared');
  }

  /**
   * Cleanup method for memory management
   * @public
   */
  destroy() {
    try {
      this.#log('info', 'Destroying CartItemReplacement instance');

      this.#replacementMappings.clear();
      this.#errors = [];
      this.#initialized = false;
      this.#isProcessing = false;

      if (this.#initializationPromise) {
        this.#initializationPromise = null;
      }

      this.#dispatchEvent('cart-replacement:destroyed', {
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      console.error('Error during CartItemReplacement cleanup:', error);
    }
  }

  /**
   * Debug method to get current state
   * @public
   * @returns {Object} Current state information
   */
  getDebugInfo() {
    return {
      initialized: this.#initialized,
      isProcessing: this.#isProcessing,
      mappingsCount: this.#replacementMappings.size,
      logsCount: this.#logs.length,
      errorCount: this.#errors.length,
      lastError: this.#errors[this.#errors.length - 1] || null,
      config: this.CONFIG,
    };
  }
}

export { CartReplacementError, NetworkError, ValidationError, PartialReplacementError };

const cartItemReplacement = new CartItemReplacement();

// Make available globally
window.cartItemReplacement = cartItemReplacement;

// Cleanup on page unload to prevent memory leaks
window.addEventListener('beforeunload', () => {
  cartItemReplacement.destroy();
});


